import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "../utils/apiError";

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

async function repairLinkedPdiAccess(user) {
  const needsRoleRepair =
    user?.role !== "pdi" && user?.organisation_role === "trainee";
  const needsInheritedOnboardingRepair =
    user?.role === "pdi"
    && user?.organisation_role === "trainee"
    && user?.onboarding_complete === true;
  if (!needsRoleRepair && !needsInheritedOnboardingRepair) {
    return user;
  }
  try {
    const repair = await axios.post(
      `${API_URL}/api/organisation/repair-my-pdi-access`,
      {},
      { withCredentials: true }
    );
    if (!repair.data?.repaired) return user;
    const refreshed = await axios.get(
      `${API_URL}/api/auth/me`,
      { withCredentials: true }
    );
    return refreshed.data;
  } catch {
    // Keep authentication available. Unresolved access is surfaced by the
    // normal workspace rather than turning a valid login into a failure.
    return user;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // P0 — auth bootstrap diagnostic (Feb 28, 2026).
  // Capture the exact failing endpoint + status when /api/auth/me
  // refuses to load the profile, so the UI can show a clear error
  // instead of an indefinite spinner that gets yanked by the
  // 401-interceptor's hard redirect. Reset on every successful
  // checkAuth.
  const [authError, setAuthError] = useState(null);

  const checkAuth = useCallback(async () => {
    const url = `${API_URL}/api/auth/me`;
    // P0 — auth bootstrap retry-with-backoff (Feb 28, 2026).
    // One retry, 500ms delay, only for transient causes:
    //   • network_or_cors (no response at all)
    //   • server_error   (5xx)
    // Do NOT retry 401 — that is a real "you are signed out"
    // signal and must surface immediately so the user is not
    // left staring at a longer spinner before the inevitable
    // session-expired redirect. Hard-capped at exactly ONE retry
    // so a persistently-broken backend can never spin forever.
    const RETRY_DELAY_MS = 500;
    const MAX_ATTEMPTS = 2; // initial + one retry
    let attempt = 0;
    let lastError = null;
    while (attempt < MAX_ATTEMPTS) {
      attempt += 1;
      try {
        const { data } = await axios.get(url, {
          withCredentials: true,
        });
        const verifiedUser = await repairLinkedPdiAccess(data);
        setUser(verifiedUser);
        setAuthError(null);
        setLoading(false);
        return;
      } catch (e) {
        lastError = e;
        const status = e?.response?.status ?? 0;
        const isTransient =
          status === 0 || (status >= 500 && status <= 599);
        if (!isTransient || attempt >= MAX_ATTEMPTS) {
          break;
        }
        // eslint-disable-next-line no-console
        console.warn("[auth-bootstrap] retry scheduled", {
          attempt, status, url, delay_ms: RETRY_DELAY_MS,
        });
        await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
      }
    }
    setUser(null);
    const status = lastError?.response?.status ?? 0;
    const detail = lastError?.response?.data?.detail;
    const reason =
      status === 0
        ? "network_or_cors"
        : status === 401
        ? "unauthenticated"
        : status >= 500 && status <= 599
        ? "server_error"
        : "other";
    // eslint-disable-next-line no-console
    console.error("[auth-bootstrap] /api/auth/me failed", {
      url, status, reason, detail, attempts: attempt,
    });
    setAuthError({ url, status, reason, detail });
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      // P0 · FOS-10a · Login redirect race fix (Feb 2026).
      // Before returning success, verify the freshly-issued session
      // cookie ROUND-TRIPS via /api/auth/me. Without this step the
      // caller can `navigate("/instructor")` before the browser has
      // committed Set-Cookie, causing the first protected-page API
      // call to 401 and the interceptor to bounce to
      // `/login?session_expired=1`. One deterministic round-trip
      // proves the cookie is accepted BEFORE any protected route
      // evaluates.
      try {
        const verify = await axios.get(
          `${API_URL}/api/auth/me`,
          { withCredentials: true }
        );
        const verifiedUser = await repairLinkedPdiAccess(verify.data);
        setUser(verifiedUser);
        return { success: true, user: verifiedUser };
      } catch {
        // Cookie failed to round-trip — treat login as failed rather
        // than silently proceeding to a page that will 401.
        return {
          success: false,
          status: 0,
          serverError: false,
          error: (
            "Signed in, but the session did not attach. "
            + "Please try again."
          ),
        };
      }
    } catch (e) {
      // P0 May 24, 2026 (operator brief): when the login attempt
      // itself returns 5xx (or the network drops), do NOT pass the
      // raw axios string "Request failed with status code 500" up
      // to the UI. It scares users and conflates server outages
      // with bad credentials. Map to a clear, truthful message that
      // tells them to retry.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (typeof status === "number" && status >= 500 && status <= 599) {
        return {
          success: false,
          status,
          serverError: true,
          error: "Login failed due to a server error. Please try again.",
        };
      }
      if (!e?.response) {
        // Network failure / Cloudflare 52x with no JSON body.
        return {
          success: false,
          status: 0,
          serverError: true,
          error: (
            "We couldn't reach the server. Check your connection and "
            + "try again."
          ),
        };
      }
      return {
        success: false,
        status,
        serverError: false,
        error: formatApiErrorDetail(detail) || e.message,
      };
    }
  };

  const register = async (email, password, name, role = "learner", referral_code = null) => {
    try {
      const { data } = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name, role, referral_code },
        { withCredentials: true }
      );
      setUser(data);
      return { success: true, user: data };
    } catch (e) {
      return {
        success: false,
        error: formatApiErrorDetail(e.response?.data?.detail) || e.message,
      };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch {
      // Ignore errors
    }
    // Wipe any role-scoped client state so the next login starts clean.
    try {
      const { clearRoleScopedStorage } = await import("../utils/roleStorage");
      clearRoleScopedStorage();
    } catch {}
    setUser(null);
  };

  const refreshUser = async () => {
    await checkAuth();
  };

  // Check if user has active subscription
  const hasActiveSubscription = () => {
    if (!user) return false;
    if (user.bypass_billing) return true;
    if (user.beta_mode) return true;
    if (user.role === "admin") return true;
    if (user.subscription_status !== "active") return false;
    if (user.subscription_end) {
      const endDate = new Date(user.subscription_end);
      return endDate > new Date();
    }
    return false;
  };

  // Check if learner needs to take assessment
  const needsAssessment = () => {
    if (!user) return false;
    if (user.role !== "learner") return false;
    return !user.assessment_completed;
  };

  const value = {
    user,
    loading,
    authError,
    login,
    register,
    logout,
    refreshUser,
    hasActiveSubscription,
    needsAssessment,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
